import classNames from 'classnames';
import styles from './beneficiaries-card.module.scss';
import CardEditButton from '../card-edit-button/card-edit-button';
import Input from '../input/input';
import Button from '../button/button';
import Card from '../card/card';
import { JSX, useEffect, useState } from 'react';
import { Beneficiary } from '../../types/beneficiary.type';
import { Plan } from '../../types/plan.type';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';
import NidInput from '../nid-input/nid-input';
import { User } from '../../types/user.type';
import { isDev } from '../../utils/env.util';
// REACT_APP_* is inlined into the bundle at build time (CRA), not read at
// runtime. isDev() only hides the button in the UI — never set these
// outside .env.development, or they end up in a production build too.
const TEST_BENEFICIARY = {
    fullName: process.env.REACT_APP_TEST_BENEFICIARY_FULL_NAME,
    nid: process.env.REACT_APP_TEST_BENEFICIARY_NID,
    email: process.env.REACT_APP_TEST_BENEFICIARY_EMAIL,
    phone: process.env.REACT_APP_TEST_BENEFICIARY_PHONE,
};

interface Props {
    plan?: Plan;
    knownUser?: User;
    currentStep: number;
    shown: boolean;
    rollUp: boolean;
    submitBeneficiaries: (beneficiaries: Beneficiary[]) => void;
    goBack: () => void;
}

const BeneficiariesCard = (props: Props) => {
    const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([
        {
            id: uuidv4(),
            fullName: '',
            nid: '',
            email: '',
            phone: '',
        },
    ]);
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [showQuestion, setShowQuestion] = useState(false);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        beneficiaries.length > 0 && validateFields();
    }, [beneficiaries]);

    useEffect(() => {
        if (props.knownUser) {
            setShowQuestion(true);
            setShowForm(false);
        } else {
            setShowForm(true);
        }
    }, [props.plan, props.knownUser]);

    const resetBeneficiaries = () => {
        setBeneficiaries([
            {
                id: uuidv4(),
                fullName: '',
                nid: '',
                email: '',
                phone: '',
            },
        ]);
    };

    const validateFields = async () => {
        const schema = yup.object({
            fullName: yup
                .string()
                .transform((val) => val?.trim())
                .min(7, 'Mínimo 7 caracteres')
                .required('Nombre completo requerido'),
            nid: yup
                .string()
                .transform((val) => val?.trim())
                .min(5, 'Mínimo 5 caracteres')
                .required('Cédula o pasaporte requerido'),
            email: yup
                .string()
                .email('Correo electrónico inválido')
                .required('Correo electrónico requerido'),
            phone: yup
                .string()
                .transform((val) => val?.trim())
                .min(7, 'Mínimo 7 dígitos')
                .required('Teléfono requerido'),
        });
        for (const beneficiary of beneficiaries) {
            const isValid = await schema.isValid(beneficiary);
            if (!isValid) return setButtonDisabled(true);
        }
        setButtonDisabled(false);
    };

    const setBeneficiaryField = (id: string, field: string, value: string) => {
        const beneficiariesCopy = Array.from(beneficiaries);
        for (const beneficiary of beneficiariesCopy) {
            if (beneficiary.id === id) {
                (beneficiary as any)[field] = value;
            }
        }
        setBeneficiaries(beneficiariesCopy);
    };

    const fillWithTestData = () => {
        const { fullName, nid, email, phone } = TEST_BENEFICIARY;
        if (!fullName || !nid || !email || !phone) {
            window.alert(
                'Faltan datos de prueba: definí REACT_APP_TEST_BENEFICIARY_FULL_NAME, ' +
                    'REACT_APP_TEST_BENEFICIARY_NID (formato V-12345678), ' +
                    'REACT_APP_TEST_BENEFICIARY_EMAIL y ' +
                    'REACT_APP_TEST_BENEFICIARY_PHONE en tu .env.development para usar Autorrellenar.'
            );
            return;
        }
        setBeneficiaries((prev) =>
            prev.map((beneficiary, idx) =>
                idx === 0
                    ? { ...beneficiary, fullName, nid, email, phone }
                    : beneficiary
            )
        );
    };

    const submitBeneficiaries = () => {
        props.submitBeneficiaries(
            beneficiaries.map((beneficiary) => ({
                ...beneficiary,
                fullName: beneficiary.fullName.trim(),
                nid: beneficiary.nid.trim(),
                email: beneficiary.email.trim(),
                phone: beneficiary.phone.trim(),
            }))
        );
    };

    const autoFillWithKnownUser = () => {
        const { knownUser } = props;
        if (!knownUser) return;
        props.submitBeneficiaries([
            {
                id: knownUser.id.toString(),
                fullName: `${knownUser.firstName} ${knownUser.lastName}`,
                nid: `${knownUser.documentType}-${knownUser.document}`,
                email: knownUser.email,
                phone: knownUser.phoneNumber,
            },
        ]);
    };

    const renderOwnerQuestion = (): JSX.Element => {
        return (
            <>
                <span
                    className={classNames({
                        [styles.cardTitle]: true,
                        [styles.active]: props.shown,
                    })}
                >
                    ¿La suscripción es para ti o para otra persona?
                </span>
                <div className={styles.knownUserSelectionButtons}>
                    <button
                        className={styles.primary}
                        onClick={autoFillWithKnownUser}
                    >
                        Para mí
                    </button>
                    <button
                        className={styles.secondary}
                        onClick={() => {
                            resetBeneficiaries();
                            setShowForm(true);
                            setShowQuestion(false);
                        }}
                    >
                        Para otra persona
                    </button>
                </div>
            </>
        );
    };

    const renderBeneficiariesForm = () => {
        return (
            <>
                <span
                    className={classNames({
                        [styles.cardTitle]: true,
                        [styles.active]: props.shown,
                    })}
                >
                    Dinos los datos del beneficiario de la suscripción:
                </span>

                {beneficiaries.map((beneficiary, idx) => (
                    <form key={idx} className={styles.beneficiaryForm}>
                        <Input
                            placeholder={'Nombre y Apellido'}
                            value={beneficiary.fullName}
                            onInput={(value) =>
                                setBeneficiaryField(
                                    beneficiary.id,
                                    'fullName',
                                    value
                                )
                            }
                        />
                        <NidInput
                            placeholder="Cédula o Pasaporte"
                            value={beneficiary.nid}
                            onInput={(value) =>
                                setBeneficiaryField(
                                    beneficiary.id,
                                    'nid',
                                    value.replaceAll('.', '')
                                )
                            }
                        />
                        <Input
                            placeholder={'Correo electrónico'}
                            value={beneficiary.email}
                            onInput={(value) =>
                                setBeneficiaryField(
                                    beneficiary.id,
                                    'email',
                                    value
                                )
                            }
                        />
                        <Input
                            placeholder={'Teléfono celular'}
                            value={beneficiary.phone}
                            onInput={(value) =>
                                setBeneficiaryField(
                                    beneficiary.id,
                                    'phone',
                                    value
                                )
                            }
                        />
                        {idx === 0 && isDev() && (
                            <span
                                className={styles.addBeneficiaryForm}
                                onClick={fillWithTestData}
                            >
                                🧪 Autorrellenar (dev)
                            </span>
                        )}
                    </form>
                ))}
                <Button
                    text={'Continuar'}
                    disabled={buttonDisabled}
                    onClick={submitBeneficiaries}
                />
            </>
        );
    };

    return (
        <Card
            className={classNames({
                [styles.card]: true,
                [styles.shown]: props.shown,
                [styles.rolledUp]: props.rollUp,
                [styles.formCard]: true,
            })}
        >
            <CardEditButton shown={props.rollUp} onClick={props.goBack} />
            {showQuestion && renderOwnerQuestion()}
            {showForm && renderBeneficiariesForm()}
        </Card>
    );
};

export default BeneficiariesCard;
